import type {
  DeviceBluetoothSerialConnectionCreateDto,
  DeviceChannelCreateDto,
  DeviceCreateDto,
  DeviceMqttConnectionCreateDto,
  DeviceWebsocketConnectionCreateDto,
} from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

import { slugKeyFromName, snapshotToMetadata, type DataloggerFormSnapshot } from "../schema/datalogger.schema";

export type ProvisioningCredentials = {
  serialNumber: string;
  apiKey: string;
  deviceUid: string;
};

function normalizeTopicSegment(value: string): string {
  return value.trim().replace(/^\/+|\/+$/g, "");
}

export function resolveMqttTelemetryTopic(snapshot: DataloggerFormSnapshot, deviceUid: string): string {
  const exactTemplate = snapshot.hardware.protocolSettings.mqttTopic?.trim();
  if (exactTemplate) {
    return normalizeTopicSegment(exactTemplate.replaceAll("{device_uid}", deviceUid));
  }

  const prefix = snapshot.hardware.protocolSettings.mqttTopicPrefix?.trim();
  if (prefix) {
    const normalizedPrefix = normalizeTopicSegment(prefix.replaceAll("{device_uid}", deviceUid));
    return `${normalizedPrefix}/${deviceUid}/telemetry`;
  }

  return `devices/${deviceUid}/telemetry`;
}

export function buildMqttConnectionPayload(
  snapshot: DataloggerFormSnapshot,
  credentials: ProvisioningCredentials,
): DeviceMqttConnectionCreateDto | null {
  if (snapshot.hardware.transmissionMode !== "mqtt") {
    return null;
  }

  const broker = snapshot.hardware.protocolSettings.mqttBroker?.trim();
  if (!broker) {
    return null;
  }

  const portRaw = snapshot.hardware.protocolSettings.mqttPort?.trim();
  const port = portRaw ? Number(portRaw) : 1883;
  return {
    broker,
    port: Number.isFinite(port) && port > 0 ? port : 1883,
    topic: resolveMqttTelemetryTopic(snapshot, credentials.deviceUid),
    tls: Boolean(snapshot.hardware.protocolSettings.mqttTls),
    username: snapshot.hardware.protocolSettings.mqttUsername?.trim() || null,
    password: snapshot.hardware.protocolSettings.mqttPassword?.trim() || null,
    publisher_client_id: snapshot.hardware.protocolSettings.mqttPublisherClientId?.trim() || credentials.deviceUid,
    qos: 0,
  };
}

export function buildWebsocketConnectionPayload(
  snapshot: DataloggerFormSnapshot,
): DeviceWebsocketConnectionCreateDto | null {
  if (snapshot.hardware.transmissionMode !== "websocket") {
    return null;
  }

  const url = snapshot.hardware.protocolSettings.websocketUrl?.trim();
  if (!url) {
    return null;
  }

  return { url };
}

export function buildBluetoothSerialConnectionPayload(
  snapshot: DataloggerFormSnapshot,
): DeviceBluetoothSerialConnectionCreateDto | null {
  if (snapshot.hardware.transmissionMode !== "bluetooth_serial") {
    return null;
  }

  const port = snapshot.hardware.protocolSettings.bluetoothPort?.trim();
  if (!port) {
    return null;
  }

  const baudRaw = snapshot.hardware.protocolSettings.bluetoothBaudRate?.trim();
  const baudRate = baudRaw ? Number(baudRaw) : 115200;
  return {
    device_name: snapshot.hardware.protocolSettings.bluetoothDeviceName?.trim() || null,
    port,
    baud_rate: Number.isFinite(baudRate) && baudRate > 0 ? baudRate : 115200,
    data_format: snapshot.hardware.protocolSettings.bluetoothDataFormat?.trim() || "TEMP:{temp},HUM:{hum}",
    line_ending: snapshot.hardware.protocolSettings.bluetoothLineEnding || "\\n",
    channel_map: {
      TEMP: "temp",
      HUM: "hum",
    },
  };
}

function mapDataType(t: DataloggerFormSnapshot["channels"][number]["dataType"]): "number" | "boolean" | "string" {
  if (t === "bool") {
    return "boolean";
  }
  if (t === "string") {
    return "string";
  }
  return "number";
}

function stripInlineDevicePhotoMetadata(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripInlineDevicePhotoMetadata);
  }
  if (!value || typeof value !== "object") {
    return value;
  }

  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (key === "device_photo_data_url") {
      continue;
    }
    out[key] = stripInlineDevicePhotoMetadata(entry);
  }
  return out;
}

export function channelsFromSnapshot(snapshot: DataloggerFormSnapshot): DeviceChannelCreateDto[] {
  return snapshot.channels.map((c) => ({
    key: c.channelKey.trim(),
    label: c.name.trim(),
    unit: c.unit.trim() ? c.unit.trim() : null,
    data_type: mapDataType(c.dataType),
    min_value: c.minValue,
    max_value: c.maxValue,
    aggregation: c.aggregations.length ? [...c.aggregations] : null,
  }));
}

/** Maps wizard state + generated credentials to `POST /devices` body (includes first-class channels). */
export function buildDeviceCreatePayload(
  snapshot: DataloggerFormSnapshot,
  tenantId: string | null,
  credentials: ProvisioningCredentials,
): DeviceCreateDto {
  const baseMeta = stripInlineDevicePhotoMetadata(snapshotToMetadata(snapshot)) as JsonObject;
  return {
    device_uid: credentials.deviceUid,
    name: snapshot.basic.name.trim() || null,
    device_category_id: snapshot.basic.deviceCategoryId || null,
    qr_public_enabled: snapshot.qrPublic.enabled,
    qr_display_fields: snapshot.qrPublic.displayFields,
    tenant_id: tenantId,
    metadata: {
      ...baseMeta,
      provisioning: {
        serial_number: credentials.serialNumber,
        api_key: credentials.apiKey,
      },
    },
    channels: channelsFromSnapshot(snapshot),
    mqtt_connection: buildMqttConnectionPayload(snapshot, credentials),
    websocket_connection: buildWebsocketConnectionPayload(snapshot),
    bluetooth_connection: buildBluetoothSerialConnectionPayload(snapshot),
  };
}

export { slugKeyFromName };
